
import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, Edit, Eye, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { GroupEvent } from './GroupEventsManager';

interface EventCardProps {
  event: GroupEvent;
  onEdit: () => void;
  onViewRegistrations: () => void;
  onRefresh: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onEdit, onViewRegistrations, onRefresh }) => {
  const { t } = useTranslation('dashboard');
  const lang = i18n.language;
  const title = getBilingualValue(lang, event.title_en, event.title_zh, event.title) || 'Untitled';
  const spotsLeft = event.max_participants - (event.registration_count || 0);

  const handleCancel = async () => {
    if (!confirm(t('events.confirmCancel'))) return;
    const { error } = await supabase
      .from('group_events')
      .update({ status: 'cancelled' })
      .eq('id', event.id);
    if (error) {
      notify.error(t('error'), t('events.cancelFailed'));
    } else {
      notify.success(t('events.cancelled'));
      onRefresh();
    }
  };

  const locationIcon = event.location_type === 'virtual' ? '🌐' : '📍';

  return (
    <div className="border rounded-lg overflow-hidden bg-card shadow-sm hover:shadow-md transition-shadow">
      {event.cover_image_url && (
        <img src={event.cover_image_url} alt={title} className="w-full h-40 object-cover" />
      )}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base line-clamp-2">{title}</h3>
          <Badge variant={event.status === 'draft' ? 'secondary' : event.status === 'cancelled' ? 'destructive' : 'default'}>
            {t(`events.status.${event.status}`)}
          </Badge>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{format(new Date(event.event_date), 'MMM d, yyyy HH:mm')}</span>
          <span className="text-xs">({event.duration_minutes} min)</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>{locationIcon} {event.location_details || t(`events.form.${event.location_type}`)}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>{event.registration_count || 0} / {event.max_participants} {t('events.registered')}</span>
          {spotsLeft <= 3 && spotsLeft > 0 && (
            <Badge variant="outline" className="text-xs">{spotsLeft} {t('events.spotsLeft')}</Badge>
          )}
          {spotsLeft <= 0 && <Badge variant="destructive" className="text-xs">{t('events.full')}</Badge>}
        </div>

        <div className="flex items-center justify-between">
          <span className="font-medium">
            {event.price > 0 ? `${event.currency} ${event.price}` : t('events.free')}
          </span>
          <Badge variant="outline">{event.service_track === 'clinical' ? t('events.form.clinical') : t('events.form.wellness')}</Badge>
        </div>

        {event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {event.tags.slice(0, 3).map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
            ))}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit className="h-3 w-3 mr-1" /> {t('events.edit')}
          </Button>
          <Button size="sm" variant="outline" onClick={onViewRegistrations}>
            <Eye className="h-3 w-3 mr-1" /> {t('events.registrations')}
          </Button>
          {event.status !== 'cancelled' && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={handleCancel}>
              <XCircle className="h-3 w-3 mr-1" /> {t('events.cancel')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
